import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type QueryValue } from "./client";
import {
  type DocumentCard,
  type DocumentHits,
  type DocumentPatch,
  type DocumentSummary,
  type Lookups,
  localizedName,
  type ModuleId,
  type PageData,
  type SearchDepth,
  type SearchMode,
  type SearchResponse,
} from "./types";

export function useLookups() {
  return useQuery({
    queryKey: ["lookups"],
    queryFn: () => api<Lookups>("/api/lookups"),
    staleTime: 10 * 60_000,
  });
}

/** Whether a module is switched on; true until the lookups arrive (the API refuses anyway). */
export function useModule(id: ModuleId): boolean {
  const lookups = useLookups();
  return lookups.data?.modules?.[id] ?? true;
}

/** Company / document-type names in the current UI language, by id. */
export function useNames() {
  const { i18n } = useTranslation();
  const lookups = useLookups();
  const company = (id: number | null | undefined): string => {
    const c = lookups.data?.companies.find((x) => x.id === id);
    return c ? localizedName(c, i18n.language) : "";
  };
  const docType = (id: number | null | undefined): string => {
    const t = lookups.data?.docTypes.find((x) => x.id === id);
    return t ? localizedName(t, i18n.language) : "";
  };
  return { company, docType, lookups };
}

/**
 * Company picker options: active companies plus whatever is currently selected even when
 * inactive (a bookmark, the back button, a document filed under a deactivated company), so a
 * chosen value never shows as an empty picker. `selected` is one id or a list; strings are the
 * Select's own values.
 */
export function useCompanyOptions(
  selected: readonly (number | string | null)[] | number | string | null | undefined,
): { value: string; label: string }[] {
  const { lookups, company } = useNames();
  const keep = new Set(
    (Array.isArray(selected) ? selected : [selected])
      .filter((v) => v !== null && v !== undefined && v !== "")
      .map(String),
  );
  return (lookups.data?.companies ?? [])
    .filter((c) => c.active || keep.has(String(c.id)))
    .map((c) => ({ value: String(c.id), label: company(c.id) }));
}

export interface SearchQuery {
  q: string;
  company: number[];
  type: number[];
  from: string | null;
  to: string | null;
  mode: SearchMode;
  depth: SearchDepth;
  page: number;
  superseded: boolean;
  sort: "relevance" | "date_desc" | "date_asc";
}

export function useSearch(query: SearchQuery | null) {
  return useQuery({
    queryKey: ["search", query],
    enabled: Boolean(query?.q.trim()),
    placeholderData: keepPreviousData,
    queryFn: ({ signal }) =>
      api<SearchResponse>("/api/search", {
        signal,
        query: {
          q: query?.q ?? "",
          company: query?.company,
          type: query?.type,
          from: query?.from,
          to: query?.to,
          mode: query?.mode,
          depth: query?.depth,
          page: query?.page,
          pageSize: 10,
          superseded: query?.superseded ? "true" : undefined,
          sort: query?.sort && query.sort !== "relevance" ? query.sort : undefined,
        } satisfies Record<string, QueryValue>,
      }),
  });
}

export function useDocumentHits(documentId: number | null, q: string, mode: SearchMode) {
  return useQuery({
    queryKey: ["document-hits", documentId, q, mode],
    enabled: documentId !== null && q.trim().length > 0,
    queryFn: ({ signal }) =>
      api<DocumentHits>(`/api/documents/${documentId}/hits`, { signal, query: { q, mode } }),
  });
}

/** Records which result was opened (ranking signal + gold-set candidates). Never blocks the UI. */
export function logClick(searchId: number, documentId: number, chunkId?: number, rank?: number) {
  void api(`/api/search/${searchId}/click`, {
    method: "POST",
    body: { documentId, chunkId, rank },
  }).catch(() => undefined);
}

export function useDocument(id: number | null) {
  return useQuery({
    queryKey: ["document", id],
    enabled: id !== null,
    queryFn: ({ signal }) => api<DocumentCard>(`/api/documents/${id}`, { signal }),
  });
}

export function usePage(documentId: number | null, pageNo: number) {
  return useQuery({
    queryKey: ["page", documentId, pageNo],
    enabled: documentId !== null && pageNo > 0,
    staleTime: 10 * 60_000,
    queryFn: ({ signal }) =>
      api<PageData>(`/api/documents/${documentId}/pages/${pageNo}`, { signal }),
  });
}

/** Documents being indexed right now; polls faster while there is something in progress. */
export function usePendingDocuments(enabled: boolean) {
  return useQuery({
    queryKey: ["documents", "pending"],
    enabled,
    queryFn: ({ signal }) =>
      api<{ count: number; items: { id: number; title: string; status: string }[] }>(
        "/api/documents/pending",
        { signal },
      ),
    refetchInterval: (q) => ((q.state.data?.count ?? 0) > 0 ? 5000 : 60_000),
  });
}

/** Admin: soft-delete or restore documents; refreshes the catalogue, search and the cards. */
export function useDeleteDocuments(action: "delete" | "restore") {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: number[]) =>
      api<{ deleted?: number[]; restored?: number[] }>(`/api/documents/${action}`, {
        method: "POST",
        body: { ids },
      }),
    onSuccess: (_r, ids) => {
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
      void queryClient.invalidateQueries({ queryKey: ["search"] });
      for (const id of ids) void queryClient.invalidateQueries({ queryKey: ["document", id] });
    },
  });
}

export function useUpdateDocument(id: number) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: DocumentPatch) =>
      api<DocumentSummary>(`/api/documents/${id}`, { method: "PATCH", body: patch }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["document", id] });
      void queryClient.invalidateQueries({ queryKey: ["search"] });
      // Catalogue, recent uploads and the header's "indexing" badge show the same fields.
      void queryClient.invalidateQueries({ queryKey: ["documents"] });
    },
  });
}
